import { fabric } from 'fabric';
import type { Resolution } from 'modules/fabric-editor';
import type { LayoutBlockData, LayoutPreset, LayoutRegionRect } from './types';

const OUTLINE_COLOR = 'rgba(99, 102, 241, 0.9)';
const FILL_COLOR = 'rgba(99, 102, 241, 0.04)';
const LABEL_COLOR = '#4338ca';
const DASH_ARRAY = [8, 4];
const STROKE_WIDTH = 1;

export interface PixelRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export function regionsToPixels(preset: LayoutPreset, resolution: Resolution): PixelRect[] {
  const W = Math.max(1, Math.round(resolution.width));
  const H = Math.max(1, Math.round(resolution.height));
  return preset.regions.map((r) => regionToPixels(r, W, H));
}

function regionToPixels(r: LayoutRegionRect, W: number, H: number): PixelRect {
  const left = Math.round(r.x * W);
  const top = Math.round(r.y * H);
  const right = Math.round((r.x + r.w) * W);
  const bottom = Math.round((r.y + r.h) * H);
  // Snap to canvas edges so neighboring cells share a pixel-perfect boundary
  // and the final column/row reaches the resolution edge despite rounding.
  const rightSnapped = isCloseTo(r.x + r.w, 1) ? W : right;
  const bottomSnapped = isCloseTo(r.y + r.h, 1) ? H : bottom;
  return {
    left,
    top,
    width: Math.max(1, rightSnapped - left),
    height: Math.max(1, bottomSnapped - top),
  };
}

function isCloseTo(value: number, target: number): boolean {
  return Math.abs(value - target) < 1e-6;
}

export function createLayoutGroup(
  data: LayoutBlockData,
  resolution: Resolution,
  preset: LayoutPreset,
): fabric.Group {
  const rects = regionsToPixels(preset, resolution);
  const children: fabric.Object[] = [];
  rects.forEach((rect, i) => {
    children.push(buildRegionRect(rect));
    children.push(buildRegionLabel(data.blocks[i]?.placeholderLabel ?? `Block ${i + 1}`, rect));
  });

  const group = new fabric.Group(children, {
    left: 0,
    top: 0,
    width: resolution.width,
    height: resolution.height,
    originX: 'left',
    originY: 'top',
    selectable: false,
    evented: false,
    hoverCursor: 'default',
    lockMovementX: true,
    lockMovementY: true,
    lockRotation: true,
    lockScalingX: true,
    lockScalingY: true,
    lockUniScaling: true,
    objectCaching: false,
    opacity: data.opacity,
    subTargetCheck: false,
  });
  group.data = { ...data };
  return group;
}

function buildRegionRect(rect: PixelRect): fabric.Rect {
  return new fabric.Rect({
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height,
    fill: FILL_COLOR,
    stroke: OUTLINE_COLOR,
    strokeWidth: STROKE_WIDTH,
    strokeDashArray: [...DASH_ARRAY],
    strokeUniform: true,
    selectable: false,
    evented: false,
    objectCaching: false,
  });
}

function buildRegionLabel(label: string, rect: PixelRect): fabric.Text {
  const fontSize = Math.max(12, Math.min(rect.width, rect.height) * 0.08);
  return new fabric.Text(label, {
    left: rect.left + rect.width / 2,
    top: rect.top + rect.height / 2,
    originX: 'center',
    originY: 'center',
    fontFamily: 'Arial',
    fontSize,
    fill: LABEL_COLOR,
    selectable: false,
    evented: false,
    objectCaching: false,
  });
}

export function getLayoutBlockData(obj: fabric.Object | undefined | null): LayoutBlockData | undefined {
  const data = obj?.data as { kind?: unknown } | undefined;
  if (!data || data.kind !== 'layout-block') return undefined;
  return obj!.data as LayoutBlockData;
}

export function findLayoutGroup(canvas: fabric.Canvas): fabric.Group | undefined {
  return canvas.getObjects().find((o) => getLayoutBlockData(o) !== undefined) as fabric.Group | undefined;
}

export function generateBlockKey(presetId: string, index: number): string {
  return `layout-block-${presetId}-${index + 1}`;
}
